import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";

export interface PointTransaction {
  id: string;
  user_id: string;
  type: "적립" | "사용";
  detail: string;
  amount: number;
  gift_item_name?: string;
  created_at: string;
}

interface UsePointsReturn {
  points: number;
  todayEarned: number;
  thisMonthEarned: number;
  thisMonthSpent: number;
  totalEarned: number;
  transactions: PointTransaction[];
  loading: boolean;
  error: string | null;
  exchangeGift: (giftName: string, cost: number) => Promise<{ success: boolean; message: string }>;
  refreshPoints: () => Promise<void>;
  awardPoints: (amount: number, detail: string) => Promise<{ success: boolean; message: string }>;
}

export function usePoints(): UsePointsReturn {
  const { user } = useAuth();
  const [points, setPoints] = useState(0);
  const [todayEarned, setTodayEarned] = useState(0);
  const [thisMonthEarned, setThisMonthEarned] = useState(0);
  const [thisMonthSpent, setThisMonthSpent] = useState(0);
  const [totalEarned, setTotalEarned] = useState(0);
  const [transactions, setTransactions] = useState<PointTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPointsData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setError(null);

      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("points")
        .eq("id", user.id)
        .maybeSingle();

      if (profileError) throw profileError;
      setPoints(profile?.points ?? 0);

      const { data: txns, error: txnError } = await supabase
        .from("point_transactions")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);

      if (txnError) throw txnError;
      setTransactions(txns || []);

      // Calculate stats
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();

      let today = 0;
      let monthEarn = 0;
      let monthSpend = 0;
      let totalEarn = 0;

      for (const t of txns || []) {
        if (t.type === "적립") {
          totalEarn += t.amount;
          if (t.created_at >= todayStart) today += t.amount;
          if (t.created_at >= monthStart) monthEarn += t.amount;
        } else {
          if (t.created_at >= monthStart) monthSpend += Math.abs(t.amount);
        }
      }

      setTodayEarned(today);
      setThisMonthEarned(monthEarn);
      setThisMonthSpent(monthSpend);
      setTotalEarned(totalEarn);
    } catch (e) {
      setError("포인트 정보를 불러오지 못했습니다");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPointsData();
  }, [fetchPointsData]);

  const exchangeGift = useCallback(async (giftName: string, cost: number) => {
    if (!user) return { success: false, message: "로그인이 필요합니다" };

    try {
      // Get current points
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("points")
        .eq("id", user.id)
        .maybeSingle();

      if (profileError) throw profileError;

      const currentPoints = profile?.points ?? 0;
      if (currentPoints < cost) {
        return { success: false, message: "포인트가 부족합니다" };
      }

      // Find matching gift item
      const { data: giftItem } = await supabase
        .from("gift_items")
        .select("id, stock")
        .eq("name", giftName)
        .eq("is_active", true)
        .maybeSingle();

      if (giftItem && giftItem.stock <= 0) {
        return { success: false, message: "재고가 소진되었습니다" };
      }

      // Deduct points
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ points: currentPoints - cost })
        .eq("id", user.id);

      if (updateError) throw updateError;

      // Decrease stock
      if (giftItem) {
        await supabase
          .from("gift_items")
          .update({ stock: giftItem.stock - 1 })
          .eq("id", giftItem.id);
      }

      // Record point transaction
      const { error: txnError } = await supabase
        .from("point_transactions")
        .insert({
          user_id: user.id,
          type: "사용",
          detail: `${giftName} 교환`,
          amount: -cost,
          gift_item_name: giftName,
        });

      if (txnError) throw txnError;

      // Create gift exchange record
      const { error: exError } = await supabase
        .from("gift_exchanges")
        .insert({
          user_id: user.id,
          gift_item_id: giftItem?.id || null,
          gift_item_name: giftName,
          points_spent: cost,
          status: "pending",
        });

      if (exError) throw exError;

      await fetchPointsData();
      return { success: true, message: `${giftName} 교환이 완료되었습니다` };
    } catch {
      return { success: false, message: "교환 중 오류가 발생했습니다" };
    }
  }, [user, fetchPointsData]);

  const refreshPoints = useCallback(async () => {
    await fetchPointsData();
  }, [fetchPointsData]);

  const awardPoints = useCallback(async (amount: number, detail: string) => {
    if (!user) return { success: false, message: "로그인이 필요합니다" };

    try {
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("points")
        .eq("id", user.id)
        .maybeSingle();

      if (profileError) throw profileError;

      const currentPoints = profile?.points ?? 0;

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ points: currentPoints + amount })
        .eq("id", user.id);

      if (updateError) throw updateError;

      const { error: txnError } = await supabase
        .from("point_transactions")
        .insert({
          user_id: user.id,
          type: "적립",
          detail,
          amount,
        });

      if (txnError) throw txnError;

      await fetchPointsData();
      return { success: true, message: `${amount}P가 적립되었습니다` };
    } catch {
      return { success: false, message: "포인트 적립 중 오류가 발생했습니다" };
    }
  }, [user, fetchPointsData]);

  return {
    points,
    todayEarned,
    thisMonthEarned,
    thisMonthSpent,
    totalEarned,
    transactions,
    loading,
    error,
    exchangeGift,
    refreshPoints,
    awardPoints,
  };
}